import { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import GoBackArtworkViewIcon from '@/assets/all-icons/go-back-artwork-view-icon';
import useGoBack from '@/hooks/useGoBack';
import useStateHandler from '@/hooks/useStateHandler';
import ThreeDotsHorizontal from '@/shared-components/icons/components/public/styles/ThreeDotsHorizontal';
import AutoMarquee from '@/shared-components/text/components/AutoMarquee';
import { getArtworkArtistDisplayName } from '@/utils/artworkArtists';
import { shouldHideCollectorInteractions } from '@/utils/artworkVisibility';

import Options from '../../../artwork-header-mobile/options';
import { ArtworkOfferCountdown } from '../../../components/ArtworkOfferElements';
import { resolveActiveViewerOffer } from '../../../utils/offer.utils';

import './ArtworkViewHeader.css';

// Fixed header displayed on mobile with the current section title and a back action.
export default function ArtworkViewHeader({ artworkData }) {
  const goBackHandler = useGoBack('/');
  const location = useLocation();
  const { pathname } = location;
  const [openOptions, setOpenOptions] = useState(false);
  const optionsAnchorRef = useRef(null);
  const { state } = useStateHandler();
  const viewerType = state?.user_session?.user_type ?? state?.user_session?.__t ?? null;
  const searchParams = new URLSearchParams(location.search);
  const holdingTypeOverride =
    searchParams.get('holding_type')
    ?? location.state?.artworkViewContext?.holdingType
    ?? null;
  const hideCollectorInteractions = shouldHideCollectorInteractions(
    artworkData,
    viewerType,
    holdingTypeOverride,
  );
  const itemSource = artworkData?._artworkViewSource;
  const itemSourceLabel = typeof itemSource?.label === 'string'
    ? itemSource.label.trim()
    : '';
  const itemSourceType = typeof itemSource?.type === 'string'
    ? itemSource.type.trim()
    : '';
  const sourceLabel = typeof location.state?.artworkViewSource?.label === 'string'
    ? location.state.artworkViewSource.label.trim()
    : '';
  const fallbackTitle = pathname?.includes('suarteworld')
    ? 'Suarteworld'
    : (() => {
      const resolvedArtist = getArtworkArtistDisplayName(artworkData);
      return resolvedArtist === '-' ? '' : resolvedArtist;
    })();
  const title =
    itemSourceLabel
    || (itemSourceType === 'suarteworld' ? 'Suarteworld' : '')
    || sourceLabel
    || fallbackTitle;
  const activeOffer = resolveActiveViewerOffer(artworkData);

  return (
    <div className="artwork-view-top-header__container element-non-selectable">
      <GoBackArtworkViewIcon
        className={`artwork-view-top-header__goBack ${pathname?.includes('suarteworld') ? 'suarteworld' : ''}`}
        onClick={goBackHandler}
      />
      {activeOffer?.expires_at ? (
        <div className="artwork-view-top-header__offer-wrapper">
          <ArtworkOfferCountdown
            expiresAt={activeOffer.expires_at}
            className="artwork-view-top-header__offer-countdown"
          />
        </div>
      ) : (
        <AutoMarquee
          className="artwork-view-top-header__title-wrapper"
          contentClassName="artwork-view-top-header__title-content"
          dependencies={[title]}
        >
          <h1 className="artwork-view-top-header__title">{title}</h1>
        </AutoMarquee>
      )}
      {!hideCollectorInteractions && (
        <div className="artwork-view-top-header__options" ref={optionsAnchorRef}>
          <ThreeDotsHorizontal
            className={`artwork-view-top-header__options-button${openOptions ? ' is-active' : ''}`}
            onClick={() => setOpenOptions(true)}
          />
          <Options
            open={openOptions}
            close={() => setOpenOptions(false)}
            artworkData={artworkData}
            anchorRef={optionsAnchorRef}
          />
        </div>
      )}
    </div>
  );
}
